import { AnalyticsContext } from '@providers/analyticsProvider';
import { ovAnalyticsEvents as analyticsEvents } from '@utils/analytics/analytics';
import { useAnalyticsContext } from '@utils/analytics/contextDetection';
import { Button, IconButton } from 'hephaestus/web';
import { AddRounded as AddIcon } from 'hephaestus/webIcons';
import { useContext, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FormModal } from '../formModal/formModal';

export type CreateNewModalProp = {
  onSubmit?: (event: any) => void | Promise<void>;
  loading?: boolean;
  children?: any[] | any;
  title?: string;
  fullWidth?: boolean;
  sx?: any;
  buttonType?: 'BUTTON' | 'ICON' | 'NONE';
  maxWidth?: string;
  disabled?: boolean;
  modalOpenDisabled?: boolean;
  iconType?: 'ADD' | 'EDIT' | 'DOT';
  buttonText?: string;
  hideSubmit?: boolean;
  modalButton?: string;
  icon?: any;
  buttonVariant?: 'outlined' | 'text' | 'contained' | 'tonal' | undefined;
  forceOpen?: boolean;
  onClose?: () => void;
  onOpen?: () => void;
};

export const CreateNewModal = ({
  onSubmit,
  loading,
  children,
  title,
  fullWidth = false,
  sx,
  buttonType = 'BUTTON',
  maxWidth = 'sm',
  buttonText,
  disabled = false,
  disableButton = false,
  icon = AddIcon,
  modalButton,
  buttonVariant = 'filled',
  forceOpen = false,
  onClose,
  onOpen,
  state,
  initialState,
  testId,
  modalType = 'generic-create',
  context,
}: {
  onSubmit: (event: any) => void;
  loading: boolean;
  children: any[] | any;
  title: string;
  fullWidth?: boolean;
  sx?: any;
  buttonType?: 'BUTTON' | 'ICON' | 'NONE';
  maxWidth?: string;
  disabled?: boolean;
  disableButton?: boolean;
  iconType?: 'ADD' | 'EDIT' | 'DOT';
  buttonText?: string;
  modalButton?: string;
  icon?: any;
  buttonVariant?: 'outlined' | 'text' | 'filled' | 'tonal' | undefined;
  forceOpen?: boolean;
  onClose?: () => void;
  onOpen?: () => void;
  state?: any;
  initialState?: any;
  testId?: string;
  modalType?: string;
  context?: string;
}) => {
  const { t } = useTranslation(['shared']);
  const { sendAnalytic } = useContext(AnalyticsContext);
  const { getModalType } = useAnalyticsContext();
  const [open, setOpen] = useState(forceOpen);

  // Automatically detect modal type based on current page
  const detectedModalType = getModalType(modalType);

  const handleClickOpen = () => {
    setOpen(true);

    // Caller-owned open analytics take precedence over built-in tracking
    if (onOpen) {
      onOpen();
      return;
    }

    sendAnalytic(analyticsEvents.modalOpen(detectedModalType, context), {
      modalTitle: title,
      buttonType,
      previousPage: document.referrer || 'direct',
      timestamp: new Date().toISOString(),
    });
  };

  const handleClose = () => {
    if (onClose) onClose();
    setOpen(false);
    // Track modal close event
    sendAnalytic(analyticsEvents.modalClose(detectedModalType, context), {
      modalTitle: title,
      previousPage: document.referrer || 'direct',
      timestamp: new Date().toISOString(),
    });
  };

  const submit = async (event: any) => {
    try {
      await onSubmit(event);
      setOpen(false);
      // Note: FormModal will handle the create event tracking
    } catch (error) {
      console.error('validation errors', error);
      // Track modal close event (covers both user cancellation and error closing)
      sendAnalytic(analyticsEvents.modalClose(detectedModalType, context), {
        modalTitle: title,
        error: error instanceof Error ? error.message : 'Unknown error',
        previousPage: document.referrer || 'direct',
        timestamp: new Date().toISOString(),
      });
    }
  };

  const StartIcon = icon;
  const muiVariant =
    buttonVariant === 'filled' || buttonVariant === 'tonal'
      ? 'contained'
      : buttonVariant;
  const muiColor = buttonVariant === 'tonal' ? 'secondary' : undefined;

  return (
    <>
      {buttonType === 'NONE' ? (
        <Button
          variant='text'
          disabled={disableButton}
          onClick={handleClickOpen}
          sx={sx}
          fullWidth={fullWidth}
          data-testid={testId ? `${testId}-add-button` : 'new-modal-add-button'}
        >
          {modalButton || t('shared:add')}
        </Button>
      ) : buttonType === 'ICON' ? (
        <IconButton
          aria-label='add'
          disabled={disableButton}
          onClick={handleClickOpen}
          data-testid={testId ? `${testId}-add-button` : 'new-modal-add-button'}
        >
          {icon}
        </IconButton>
      ) : (
        <Button
          variant={muiVariant}
          color={muiColor}
          disabled={disableButton}
          onClick={handleClickOpen}
          sx={sx}
          fullWidth={fullWidth}
          startIcon={StartIcon ? <StartIcon /> : undefined}
          data-testid={testId ? `${testId}-add-button` : 'new-modal-add-button'}
        >
          {modalButton || t('shared:add')}
        </Button>
      )}
      <FormModal
        onSubmit={submit}
        loading={loading}
        disabled={disabled}
        children={children}
        title={title}
        open={open}
        handleClose={handleClose}
        formButton={buttonText || t('shared:create')}
        maxWidth={maxWidth}
        state={state}
        initialState={initialState}
        testId={testId}
      />
    </>
  );
};

export default CreateNewModal;
